import { CompanyAsyncSelect } from '@/components/features/Projects/components/CompanyAsyncSelect';
import { UserSelect } from '@/components/global/UserSelect';
import { AddressMapCard } from '@/components/global/address/AddressMapCard';
import { AddressSearchInput } from '@/components/global/address/AddressSearchInput';
import { GoogleMapsProvider } from '@/components/global/address/GoogleMapsProviderSingleton';
import {
    ResponsiveModal,
    ResponsiveModalContent,
    ResponsiveModalHeader,
    ResponsiveModalTitle,
    ResponsiveModalTrigger,
} from '@/components/ui/responsive-modal';
import { Button } from '@/components/ui/shadcn/button';
import { Calendar } from '@/components/ui/shadcn/calendar';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/shadcn/form';
import { Input } from '@/components/ui/shadcn/input';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/shadcn/popover';
import { useAbilities } from '@/hooks/use-abilities';
import { useTranslations } from '@/hooks/useTranslations';
import { cn } from '@/lib/utils';
import { Project } from '@/types';
import { standardSchemaResolver } from '@hookform/resolvers/standard-schema';
import { router, usePage } from '@inertiajs/react';
import { format } from 'date-fns';
import { CalendarIcon, Edit, Euro } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { route } from 'ziggy-js';
import * as z from 'zod';

function buildProjectEditAddressLabel(project: Project): string {
    const trimmed = project.address?.trim();
    if (trimmed) {
        return trimmed;
    }
    const street = project.address_street;
    const houseNumber = project.address_house_number;
    if (street || houseNumber) {
        return [street, houseNumber].filter(Boolean).join(' ');
    }
    const city = project.address_city;
    const postal = project.address_postal_code;
    const municipality = project.address_municipality;
    const parts = [city, postal, municipality].filter(Boolean);
    return parts.length > 0 ? parts.join(', ') : '';
}

const normalizeCurrencyInput = (value: string): string => value.replace(/\s/g, '').replace(',', '.');

const formatCurrencyInput = (value: string | number | null | undefined): string => {
    const normalized = String(value ?? '')
        .replace(/\s/g, '')
        .replace(',', '.')
        .replace(/[^\d.]/g, '');

    if (!normalized) {
        return '';
    }

    const [integerPart, ...decimalParts] = normalized.split('.');
    const groupedInteger = integerPart.replace(/^0+(?=\d)/, '').replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
    const decimalPart = decimalParts.join('');

    return decimalParts.length > 0 ? `${groupedInteger || '0'}.${decimalPart}` : groupedInteger;
};

interface Props {
    project: Project;
    users: { id: string; name: string }[];
    open?: boolean;
    onOpenChange?: (open: boolean) => void;
}

export default function EditProjectModal({ project, open: controlledOpen, onOpenChange }: Props) {
    const { t } = useTranslations();
    const [internalOpen, setInternalOpen] = useState(false);
    const isControlled = controlledOpen !== undefined && onOpenChange !== undefined;
    const open = isControlled ? controlledOpen : internalOpen;
    const setOpen = (value: boolean) => {
        if (isControlled && onOpenChange) {
            onOpenChange(value);
        } else {
            setInternalOpen(value);
        }
    };
    const [startDateOpen, setStartDateOpen] = useState(false);
    const [endDateOpen, setEndDateOpen] = useState(false);
    const { isAdministrator } = useAbilities();
    const page = usePage<{ auth?: { user?: { id?: string } } }>();
    const currentUserId = page.props.auth?.user?.id;
    // UpdateProjectRequest strips company, country, signature/start/end dates and budget
    // for non-administrators, so those stay disabled here rather than silently no-op on
    // save. Owner is admin-only by UI policy.
    const canEditCompany = isAdministrator && !project.lead_id;
    // "Object name" (contract_name) is editable only by an Administrator, the project's
    // owner, or the user who created it — mirrors the gating in UpdateProjectRequest.
    const canEditContractName =
        isAdministrator || (currentUserId !== undefined && (project.project_owner_id === currentUserId || project.created_by === currentUserId));
    const invalidFieldClassName = 'border-destructive ring-destructive/20 ring-[3px]';
    const requiredMark = <span className="text-destructive">*</span>;

    const formSchema = useMemo(
        () =>
            z.object({
                contract_name: z
                    .string()
                    .min(1, t('projects.create.validation.contract_name_required', 'Contract name is required'))
                    .max(255, t('projects.create.validation.contract_name_max', 'Contract name cannot exceed 255 characters')),
                company_id: z.string().min(1, t('projects.create.validation.company_required', 'Company is required')),
                address: z.string().min(1, t('projects.forms.edit_project.validation.address_required', 'Address is required')),
                address_lat: z.number().nullable().optional(),
                address_lng: z.number().nullable().optional(),
                address_street: z.string().nullable().optional(),
                address_house_number: z.string().nullable().optional(),
                address_postal_code: z.string().nullable().optional(),
                address_city: z.string().nullable().optional(),
                address_municipality: z.string().nullable().optional(),
                address_country: z.string().nullable().optional(),
                project_owner_id: z.string().min(1, t('projects.forms.edit_project.validation.owner_required', 'Project owner is required')),
                start_date: z.string().min(1, t('projects.forms.edit_project.validation.start_required', 'Start date is required')),
                end_date: z.string().min(1, t('projects.forms.edit_project.validation.end_required', 'End date is required')),
                budget: z
                    .string()
                    .min(1, t('projects.forms.edit_project.validation.budget_required', 'Budget is required'))
                    .refine((value) => !Number.isNaN(Number(normalizeCurrencyInput(value))) && Number(normalizeCurrencyInput(value)) >= 0, {
                        message: t('projects.create.validation.budget_invalid', 'Budget must be a valid number'),
                    }),
            }),
        [t],
    );

    type FormValues = z.infer<typeof formSchema>;

    const defaultValues = useMemo(
        (): FormValues => ({
            contract_name: project.contract_name ?? '',
            company_id: project.company_id ? project.company_id.toString() : '',
            address: buildProjectEditAddressLabel(project),
            address_lat: project.address_lat ?? null,
            address_lng: project.address_lng ?? null,
            address_street: project.address_street ?? null,
            address_house_number: project.address_house_number ?? null,
            address_postal_code: project.address_postal_code ?? null,
            address_city: project.address_city ?? null,
            address_municipality: project.address_municipality ?? null,
            address_country: project.address_country ?? null,
            project_owner_id: project.project_owner_id ? project.project_owner_id.toString() : '',
            start_date: project.start_date,
            end_date: project.end_date,
            budget: formatCurrencyInput(project.sales_price ?? 0),
        }),
        [project],
    );

    const form = useForm<FormValues>({
        resolver: standardSchemaResolver(formSchema),
        mode: 'onTouched',
        defaultValues,
    });

    useEffect(() => {
        form.register('address_lat');
        form.register('address_lng');
        form.register('address_street');
        form.register('address_house_number');
        form.register('address_postal_code');
        form.register('address_city');
        form.register('address_municipality');
        form.register('address_country');
    }, [form]);

    useEffect(() => {
        if (open) {
            form.reset(defaultValues);
        }
    }, [open, defaultValues, form]);

    const watchedAddress = form.watch('address');
    const watchedAddressLat = form.watch('address_lat');
    const watchedAddressLng = form.watch('address_lng');

    function onSubmit(values: FormValues) {
        const data = {
            ...values,
            project_owner_id: values.project_owner_id,
            budget: Number(normalizeCurrencyInput(values.budget)),
        };
        delete (data as Record<string, unknown>).address;

        // Never submit the admin-only fields so locked values can't be tampered with
        // client-side.
        if (!isAdministrator) {
            delete (data as Record<string, unknown>).company_id;
            delete (data as Record<string, unknown>).project_owner_id;
            delete (data as Record<string, unknown>).start_date;
            delete (data as Record<string, unknown>).end_date;
            delete (data as Record<string, unknown>).budget;
        }

        if (!canEditContractName) {
            delete (data as Record<string, unknown>).contract_name;
        }

        router.put(route('projects.update', project.id), data, {
            onSuccess: () => {
                setOpen(false);
                router.reload({ only: ['project'] });
            },
            onError: (errors) => {
                console.error('Project update failed:', errors);
            },
        });
    }

    function onInvalid() {
        toast.error(t('projects.forms.edit_project.validation.resolve_errors', 'Please fill in all required fields.'));
    }

    return (
        <ResponsiveModal open={open} onOpenChange={setOpen} variant="sheet">
            {!isControlled ? (
                <ResponsiveModalTrigger asChild>
                    <Button variant="default">
                        <Edit className="mr-2" />
                        {t('projects.forms.edit_project.button', 'Edit Project')}
                    </Button>
                </ResponsiveModalTrigger>
            ) : null}
            {/* The form already owns the scrolling middle, so no ResponsiveModalBody here. */}
            <ResponsiveModalContent size="lg" className="p-6 md:p-6">
                <ResponsiveModalHeader className="shrink-0">
                    <ResponsiveModalTitle>{t('projects.forms.edit_project.title', 'Edit Project')}</ResponsiveModalTitle>
                </ResponsiveModalHeader>

                <Form {...form}>
                    {/* The form scrolls on short viewports; negative margin gives focus rings room inside the scroll container. */}
                    <form onSubmit={form.handleSubmit(onSubmit, onInvalid)} className="-mx-1 min-h-0 flex-1 space-y-4 overflow-y-auto px-1">
                        <FormField
                            control={form.control}
                            name="contract_name"
                            render={({ field, fieldState }) => (
                                <FormItem>
                                    <FormLabel>
                                        {t('projects.create.fields.contract_name', 'Contract Name')} {requiredMark}
                                    </FormLabel>
                                    <FormControl>
                                        <Input
                                            {...field}
                                            type="text"
                                            disabled={!canEditContractName}
                                            aria-invalid={!!fieldState.error}
                                            className={cn(fieldState.error && invalidFieldClassName)}
                                        />
                                    </FormControl>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />

                        <FormField
                            control={form.control}
                            name="company_id"
                            render={({ field, fieldState }) => (
                                <FormItem>
                                    <FormLabel>
                                        {t('projects.create.fields.company', 'Company')} {requiredMark}
                                    </FormLabel>
                                    <FormControl>
                                        <CompanyAsyncSelect
                                            value={field.value}
                                            onValueChange={field.onChange}
                                            placeholder={t('projects.create.placeholders.company', 'Select a company')}
                                            disabled={!canEditCompany}
                                            className={cn(fieldState.error && invalidFieldClassName)}
                                        />
                                    </FormControl>
                                    {project.lead_id ? (
                                        <p className="text-muted-foreground text-xs">
                                            {t(
                                                'projects.forms.edit_project.hints.company_locked',
                                                'Company cannot be changed when this project came from a lead.',
                                            )}
                                        </p>
                                    ) : !isAdministrator ? (
                                        <p className="text-muted-foreground text-xs">
                                            {t('projects.forms.edit_project.hints.admin_only', 'Only administrators can change this field.')}
                                        </p>
                                    ) : null}
                                    <FormMessage />
                                </FormItem>
                            )}
                        />

                        <FormField
                            control={form.control}
                            name="project_owner_id"
                            render={({ field, fieldState }) => (
                                <FormItem>
                                    <FormLabel>
                                        {t('projects.forms.edit_project.labels.project_owner', 'Project Owner')} {requiredMark}
                                    </FormLabel>
                                    <FormControl>
                                        <UserSelect
                                            value={field.value || null}
                                            onValueChange={(value) => field.onChange(value ?? '')}
                                            placeholder={t('projects.forms.edit_project.placeholders.select_project_owner', 'Select project owner')}
                                            allowClear={false}
                                            disabled={!isAdministrator}
                                            seedUsers={project.owner ? [project.owner] : []}
                                            className={cn('justify-start text-left', fieldState.error && invalidFieldClassName)}
                                        />
                                    </FormControl>
                                    {!isAdministrator ? (
                                        <p className="text-muted-foreground text-xs">
                                            {t('projects.forms.edit_project.hints.admin_only', 'Only administrators can change this field.')}
                                        </p>
                                    ) : null}
                                    <FormMessage />
                                </FormItem>
                            )}
                        />

                        <div className="grid items-start gap-4 md:grid-cols-2">
                            <FormField
                                control={form.control}
                                name="start_date"
                                render={({ field, fieldState }) => (
                                    <FormItem>
                                        <FormLabel>
                                            {t('projects.forms.edit_project.labels.start_date', 'Start Date')} {requiredMark}
                                        </FormLabel>
                                        <Popover open={startDateOpen} onOpenChange={setStartDateOpen}>
                                            <PopoverTrigger asChild disabled={!isAdministrator}>
                                                <FormControl>
                                                    <Button
                                                        variant="outline"
                                                        disabled={!isAdministrator}
                                                        className={cn(
                                                            'w-full justify-start text-left font-normal',
                                                            !field.value && 'text-muted-foreground',
                                                            fieldState.error && invalidFieldClassName,
                                                        )}
                                                    >
                                                        <CalendarIcon className="mr-2 h-4 w-4" />
                                                        {field.value ? (
                                                            format(new Date(field.value), 'yyyy-MM-dd')
                                                        ) : (
                                                            <span>{t('projects.forms.edit_project.placeholders.pick_date', 'Pick a date')}</span>
                                                        )}
                                                    </Button>
                                                </FormControl>
                                            </PopoverTrigger>
                                            <PopoverContent className="w-auto p-0" align="start">
                                                <Calendar
                                                    mode="single"
                                                    selected={field.value ? new Date(field.value) : undefined}
                                                    onSelect={(date) => {
                                                        field.onChange(date ? format(date, 'yyyy-MM-dd') : '');
                                                        setStartDateOpen(false);
                                                    }}
                                                    initialFocus
                                                />
                                            </PopoverContent>
                                        </Popover>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />

                            <FormField
                                control={form.control}
                                name="end_date"
                                render={({ field, fieldState }) => (
                                    <FormItem>
                                        <FormLabel>
                                            {t('projects.forms.edit_project.labels.end_date', 'End Date')} {requiredMark}
                                        </FormLabel>
                                        <Popover open={endDateOpen} onOpenChange={setEndDateOpen}>
                                            <PopoverTrigger asChild disabled={!isAdministrator}>
                                                <FormControl>
                                                    <Button
                                                        variant="outline"
                                                        disabled={!isAdministrator}
                                                        className={cn(
                                                            'w-full justify-start text-left font-normal',
                                                            !field.value && 'text-muted-foreground',
                                                            fieldState.error && invalidFieldClassName,
                                                        )}
                                                    >
                                                        <CalendarIcon className="mr-2 h-4 w-4" />
                                                        {field.value ? (
                                                            format(new Date(field.value), 'yyyy-MM-dd')
                                                        ) : (
                                                            <span>{t('projects.forms.edit_project.placeholders.pick_date', 'Pick a date')}</span>
                                                        )}
                                                    </Button>
                                                </FormControl>
                                            </PopoverTrigger>
                                            <PopoverContent className="w-auto p-0" align="start">
                                                <Calendar
                                                    mode="single"
                                                    selected={field.value ? new Date(field.value) : undefined}
                                                    onSelect={(date) => {
                                                        field.onChange(date ? format(date, 'yyyy-MM-dd') : '');
                                                        setEndDateOpen(false);
                                                    }}
                                                    initialFocus
                                                />
                                            </PopoverContent>
                                        </Popover>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />
                        </div>

                        <FormField
                            control={form.control}
                            name="budget"
                            render={({ field, fieldState }) => (
                                <FormItem>
                                    <FormLabel>
                                        {t('projects.forms.edit_project.labels.budget', 'Budget')} {requiredMark}
                                    </FormLabel>
                                    <FormControl>
                                        <div className="relative">
                                            <Euro className="text-muted-foreground pointer-events-none absolute top-1/2 left-3 h-4 w-4 -translate-y-1/2" />
                                            <Input
                                                {...field}
                                                type="text"
                                                inputMode="decimal"
                                                disabled={!isAdministrator}
                                                aria-invalid={!!fieldState.error}
                                                className={cn('pl-9', fieldState.error && invalidFieldClassName)}
                                                onChange={(event) => {
                                                    field.onChange(formatCurrencyInput(event.target.value));
                                                }}
                                            />
                                        </div>
                                    </FormControl>
                                    {!isAdministrator ? (
                                        <p className="text-muted-foreground text-xs">
                                            {t('projects.forms.edit_project.hints.admin_only', 'Only administrators can change this field.')}
                                        </p>
                                    ) : null}
                                    <FormMessage />
                                </FormItem>
                            )}
                        />

                        <GoogleMapsProvider>
                            <FormField
                                control={form.control}
                                name="address"
                                render={({ field, fieldState }) => (
                                    <FormItem>
                                        <FormLabel>
                                            {t('projects.forms.edit_project.labels.address', 'Address')} {requiredMark}
                                        </FormLabel>
                                        <FormControl>
                                            <div
                                                className={cn(
                                                    'space-y-4',
                                                    fieldState.error &&
                                                        '[&_input]:border-destructive [&_input]:ring-destructive/20 [&_input]:ring-[3px]',
                                                )}
                                            >
                                                <AddressSearchInput
                                                    value={field.value ?? ''}
                                                    onChange={(value) => {
                                                        field.onChange(value);
                                                        form.setValue('address_lat', null);
                                                        form.setValue('address_lng', null);
                                                        form.setValue('address_street', null);
                                                        form.setValue('address_house_number', null);
                                                        form.setValue('address_postal_code', null);
                                                        form.setValue('address_city', null);
                                                        form.setValue('address_municipality', null);
                                                        form.setValue('address_country', null);
                                                    }}
                                                    onAddressSelect={(address) => {
                                                        field.onChange(address.label);
                                                        form.setValue('address_lat', address.lat ?? null);
                                                        form.setValue('address_lng', address.lng ?? null);
                                                        if (address.street) {
                                                            form.setValue('address_street', address.street);
                                                        }
                                                        if (address.house_number) {
                                                            form.setValue('address_house_number', address.house_number);
                                                        }
                                                        if (address.city) {
                                                            form.setValue('address_city', address.city);
                                                        }
                                                        if (address.postal_code) {
                                                            form.setValue('address_postal_code', address.postal_code);
                                                        }
                                                    }}
                                                    placeholder={t(
                                                        'projects.forms.edit_project.placeholders.address',
                                                        'Enter or search for address...',
                                                    )}
                                                />
                                                {watchedAddressLat != null && watchedAddressLng != null && (
                                                    <AddressMapCard address={watchedAddress || ''} lat={watchedAddressLat} lng={watchedAddressLng} />
                                                )}
                                            </div>
                                        </FormControl>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />
                        </GoogleMapsProvider>

                        {/* Sticky so Save stays reachable without scrolling to the end of a long form. */}
                        <div className="bg-background sticky bottom-0 flex justify-end space-x-2 pt-4 pb-1">
                            <Button type="button" variant="outline" onClick={() => setOpen(false)}>
                                {t('projects.forms.edit_project.buttons.cancel', 'Cancel')}
                            </Button>
                            <Button type="submit">{t('projects.forms.edit_project.buttons.save_changes', 'Save Changes')}</Button>
                        </div>
                    </form>
                </Form>
            </ResponsiveModalContent>
        </ResponsiveModal>
    );
}
